import Core from "@/modules/core/service/Core";

const url = {
    clipboard: "/api/incident/clipboard/{0}/entry",
    paste    : "/api/incident/clipboard/{0}/entry/{1}",
}

const I18n = {
    success: "clipboard.paste.success"
}

/**
 * Checks if the given incident accepts paste operations from a clipboard.
 *
 * @param incident the incident to check
 * @param clipboarddata the data received from a clipboard
 * @returns true, if the clipboard data is accepted
 */
const acceptClipboard = function (incident, clipboarddata) {
    if (clipboarddata.incidentId === incident.id) {
        return {
            valid  : false,
            message: "clipboard.drop.incident.same"
        };
    }

    if (clipboarddata.typeId !== incident.typeId) {
        return  {
            valid  : false,
            message: "clipboard.drop.incident.type"
        };
    }

    if (incident.sites.length === 1 && clipboarddata.unit) {
        if (incident.sites[0].id === clipboarddata.unit.id) {
            return  {
                valid  : false,
                message: "clipboard.drop.incident.unit"
            };
        }
    }

    return {
        valid  : true,
        message: null
    };
};

/**
 *
 * @param incident
 * @param message
 * @param types
 * @returns {boolean}
 */
const acceptTypes = function (incident, message, types) {
    if (message.type.id === "DEFAULT") {
        return {
            valid  : true,
            message: null
        };
    }

    const valid = types.filter(rule => {
        if (rule.message === message.type.id) {
            return !rule.incident || rule.incident === incident.typeName;
        }

        return false;
    }).length > 0;

    return {
        valid  : valid,
        message: "inb.drop.type"
    }
}

/**
 *
 * @param incident
 * @param message
 * @param types
 * @param ignoreSites
 * @returns {boolean}
 */
const acceptInbox = function (incident, message, types, ignoreSites) {
    if (!message.sites || message.sites.length === 0 || ignoreSites) {
        if (!message.customer) {
            return acceptTypes(incident, message, types);
        }

        if (message.customer.id === incident.sites[0].customer.id) {
            return acceptTypes(incident, message, types);
        } else {
            return {
                valid  : false,
                message: "inb.drop.customer"
            };
        }
    }

    if (message.sites.length > 0) {
        let found = false;
        message.sites.forEach(site => {
            if (incident.sites.filter (s => s.id === site.id).length > 0) {
                found = true;
            }
        })

        if (!found) {
            return {
                valid  : false,
                message: "inb.drop.unit"
            };
        }
    }

    return acceptTypes(incident, message, types);
}

/**
 *
 * @param component
 * @param incident
 * @param unit
 */
const success = function (component, incident, unit) {
    Core.message.hint(component, component.$t(I18n.success));
    component.$emit("pasted", {
        data: incident,
        unit: unit
    });
}

/**
 * Performs the paste request
 *
 * @param clipboardEntryId the ID of the clipboard entry, which will be pasted to the target incident.
 * @param unit the unit data of the clipboard entry.
 * @param targetIncidentId the ID of the target incident.
 * @param force true if warnings are to be ignored
 * @param incident current incident
 * @returns {Promise<*>}
 */
const paste = function (clipboardEntryId, unit, targetIncidentId, force, incident) {
    return Core.method.put(url.paste, {
        targetId: targetIncidentId,
        force   : force,
        incident: incident
    }, unit.customer.id, clipboardEntryId);
};

/**
 * Creates an HTML list for the given validation errors.
 *
 * @param conflictJson the error object returned by the server.
 * @returns null, if the server side validation was successfull, otherwise an HTML list for the given validation errors.
 */
const conflicts = function (conflictJson) {
    if (!conflictJson) {
        return null;
    }

    let conflicts = "<ul>";
    const conflictList = JSON.parse(conflictJson);
    conflictList.forEach ((conflic) => {
        conflicts = conflicts + "<li>" + conflic["stepname"] + " (" + conflic.source + " vs. " + conflic.target +")</li>";
    });

    return conflicts + "</ul>";
};

/**
 *
 * @param component
 * @param error
 * @param clipboardEntryId
 * @param unit
 * @param targetIncidentId
 * @param parameter
 */
const onForcePaste = function (component, error, clipboardEntryId, unit, targetIncidentId, parameter) {
    Core.message.confirm(
        component,
        component.$t(error.id + ".query"),
        component.$t(error.id + ".text", parameter),
        Core.message.Default,
        (value) => {
            if (!value) {
                return;
            }

            paste(clipboardEntryId, unit, targetIncidentId, true, null)
                .then((data)=> {
                    success(component, data, unit);
                })
                .catch ((error) => {
                    Core.message.popup(component, error.id, error.parameter);
                });
        });
};

export default {
    /**
     *
     * @param dragData
     * @param incident
     * @param types
     * @param clipboardAllowed
     * @param ignoreSites
     * @returns {boolean|*}
     */
    accept (dragData, incident, types, clipboardAllowed, ignoreSites) {
        if (!dragData) {
            return {
                valid  : false,
                message: null
            };
        }

        if (!(dragData instanceof Object)) {
            return {
                valid  : false,
                message: null
            };
        }

        switch (dragData.type) {
            case 'clipboard':
                if (!clipboardAllowed) {
                    return {
                        valid  : false,
                        message: "clipboard.drop.incident.notallowed"
                    };
                }

                return acceptClipboard(incident, dragData.data);
            case 'inbox':
                return acceptInbox(incident, dragData.data, types, ignoreSites);
        }

        return {
            valid  : false,
            message: null
        };
    },

    /**
     *
     * @param customerId
     * @returns {Promise<*>}
     */
    list (customerId) {
        return Core.method.get(url.clipboard, customerId);
    },

    /**
     *
     * @param incidentId
     * @param unit
     * @returns {Promise<*>}
     */
    add (incidentId, unit) {
        return Core.method.post(url.clipboard, {
                incidentId: incidentId,
                unitId    : unit.id
            }, unit.customer.id
        );
    },

    /**
     *
     * @param entry
     * @param unit
     * @returns {Promise<*>}
     */
    remove (entry, unit) {
        return Core.method.delete(url.paste, null, unit.customer.id, entry);
    },

    /**
     * Paste the given clipboard entry to the giben target incident.
     *
     * @param component the VUE component, which called this function.
     * @param clipboardEntryId the ID of the clipboard entry, which will be pasted to the target incident.
     * @param unit the unit data of the clipboard entry.
     * @param targetIncidentId the ID of the target incident.
     * @param incident current incident
     */
    paste (component, clipboardEntryId, unit, targetIncidentId, incident) {
        paste (clipboardEntryId, unit, targetIncidentId, false, incident)
            .then((data) => {
                success(component, data, unit);
            })
            .catch ((error) => {
                switch (error.id) {
                    case "clipboard.paste.unitexists":
                        onForcePaste (component, error, clipboardEntryId, unit, targetIncidentId, [ error.parameter[0] ]);
                        break;
                    case "clipboard.paste.unitexistsmanufacturer":
                        onForcePaste (component, error, clipboardEntryId, unit, targetIncidentId, [ error.parameter[0], conflicts (error.parameter[1]) ]);
                        break;
                    case "clipboard.paste.manufacturer":
                        onForcePaste (component, error, clipboardEntryId, unit, targetIncidentId, [ conflicts (error.parameter[0]) ]);
                        break;
                    default:
                        Core.message.popup(component, error.id, error.parameter);
                }
            });
    },
}